import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";
import { useRef, useState } from "react";

export const HeroSection = () => {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      setShowOverlay(false);
    }
  };

  const handleFullscreen = () => {
    const element = videoContainerRef.current;
    if (element) {
      if (element.requestFullscreen) element.requestFullscreen();
      else if ((element as any).webkitRequestFullscreen) (element as any).webkitRequestFullscreen();
      else if ((element as any).msRequestFullscreen) (element as any).msRequestFullscreen();
      else if (videoRef.current && (videoRef.current as any).webkitEnterFullscreen) (videoRef.current as any).webkitEnterFullscreen();
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
        setShowOverlay(false);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-start pt-32 md:pt-40 pb-8 md:pb-16 overflow-hidden"
      style={{ background: "#FAFAFA" }}
    >
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display font-extrabold tracking-tight mb-4 md:mb-6 leading-[1.1]" style={{ color: "#111827", fontSize: "clamp(32px, 5vw, 56px)" }}>
            <span style={{ color: "#F59E0B" }}>500 Followers</span> Is Enough
            <br />
            To Start Making Money.
          </h1>

          <p className="md:text-lg max-w-2xl mx-auto mb-6 md:mb-8 px-2" style={{ color: "#4B5563", fontSize: "clamp(14px, 1.5vw, 18px)" }}>
            Most creators wait until they're "big enough" to monetise. AI Storycraft Studio turns any audience — 500, 5,000, or 500,000 followers — into real income. One click builds your complete digital product. No experience. No waiting.
          </p>

          {/* Video Player */}
          <div className="relative w-full max-w-[800px] mx-auto mb-6 md:mb-8">
            <div ref={videoContainerRef} className="relative overflow-hidden aspect-video rounded-2xl" style={{ border: "1px solid #E5E7EB", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <video
                ref={videoRef}
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover z-10"
                poster="https://res.cloudinary.com/dh1axspwh/video/upload/so_0/v1768145897/Sales_Video_Final_ddbpsf.jpg"
                onEnded={() => setIsPlaying(false)}
              >
                <source src="https://res.cloudinary.com/dh1axspwh/video/upload/v1768145897/Sales_Video_Final_ddbpsf.mp4" type="video/mp4" />
              </video>

              {showOverlay && (
                <button
                  onClick={handlePlayClick}
                  className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 transition-opacity hover:bg-black/30"
                  aria-label="Play video"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 flex items-center justify-center shadow-2xl hover:scale-105 transition-transform">
                    <Play className="w-7 h-7 md:w-9 md:h-9 ml-1" style={{ color: "#7C3AED" }} fill="#7C3AED" />
                  </div>
                </button>
              )}

              {!showOverlay && (
                <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 z-20 flex gap-1.5 md:gap-2">
                  <button onClick={togglePlayPause} className="p-2.5 md:p-2 rounded-lg bg-black/60 hover:bg-black/80 text-white/90 hover:text-white transition-all touch-manipulation" aria-label={isPlaying ? "Pause" : "Play"}>
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <button onClick={toggleMute} className="p-2.5 md:p-2 rounded-lg bg-black/60 hover:bg-black/80 text-white/90 hover:text-white transition-all touch-manipulation" aria-label={isMuted ? "Unmute" : "Mute"}>
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <button onClick={handleFullscreen} className="p-2.5 md:p-2 rounded-lg bg-black/60 hover:bg-black/80 text-white/90 hover:text-white transition-all touch-manipulation" aria-label="Fullscreen">
                    <Maximize className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mb-2 px-2">
            <a href="#pricing" className="inline-block w-full md:w-auto">
              <Button variant="hero" size="xl" className="group text-base md:text-lg px-8 py-5 md:px-12 md:py-6 w-full md:w-auto max-w-[400px] btn-interactive">
                Start Your Free Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
          <p className="text-xs mb-8 md:mb-12" style={{ color: "#9CA3AF" }}>
            No credit card required. 7-day free trial. Cancel anytime.
          </p>
        </div>
      </div>
    </section>
  );
};
